import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route workflow-report started_at + now anchors through clock.
import { clock } from '@/utils/clock';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { checkActiveTemplate } from '@/lib/inspections/templateCheck';
import { toast } from '@/hooks/use-toast';
import { orderNotes } from '@/lib/notes/noteProvenance';

// Types
export interface WorkflowReport {
  id: string;
  ticket_id: string;
  shop_id: string;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface WorkflowComplaint {
  id: string;
  workflow_report_id: string;
  ticket_id: string;
  shop_id: string;
  text: string;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  author_name?: string;
}

export interface WorkflowNote {
  id: string;
  workflow_report_id: string;
  ticket_id: string;
  shop_id: string;
  note_text: string;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  author_name?: string;
  // WS-NOTES (FD-10): accountable-editing provenance (20270207100000).
  edited_at?: string | null;
  edited_by?: string | null;
  edit_count?: number;
  deleted_at?: string | null;
  deleted_by?: string | null;
  delete_reason?: string | null;
  /** The author's roles in the shop when the note was written (null for notes older than the snapshot). */
  author_roles?: string[] | null;
  editor_name?: string;
  deleter_name?: string;
  // WS-NOTES (FD-09): which day of the rollover chain the note was written on.
  origin?: NoteChainMember;
}

/**
 * One ticket of the logical repair, as ticket_note_chain returns it (rollover days; Oct 20 addendum N1: also split
 * parts and the original they superseded, merge sources, the L3 return continuation). The extra fields are optional
 * so a reply from an older server still reads as a plain rollover chain.
 */
export type NoteChainRelation =
  | 'original' | 'rollover_day' | 'split_original' | 'split_part' | 'merged' | 'merged_source' | 'return_continuation';
export interface NoteChainMember {
  ticket_id: string;
  chain_position: number;
  simple_ticket_number: number | null;
  scheduled_date: string | null;
  is_current: boolean;
  status: string | null;
  relation?: NoteChainRelation | null;
  /** Day within its own rollover run (1 = the run's first ticket). */
  day_number?: number | null;
  split_sequence?: number | null;
  split_count?: number | null;
  /** The ticket this one hangs from (split parent, rollover predecessor, the ticket picked up before a return). */
  linked_from?: string | null;
  /** The first ticket of this ticket's rollover run. */
  run_root?: string | null;
}

/** One preserved revision of a note (visible to managers/owners, the author and the editor — RLS decides). */
export interface WorkflowNoteRevision {
  id: string;
  note_id: string;
  revision_no: number;
  action: 'edit' | 'delete';
  previous_text: string;
  new_text: string | null;
  editor_id: string;
  editor_roles: string[] | null;
  editor_is_author: boolean;
  reason: string;
  created_at: string;
  editor_name?: string;
}

/**
 * FD-09: the notes of every day of the ticket's rollover chain, oldest first. The chain (and which days the
 * caller may see) comes from the server (ticket_note_chain); the notes themselves are read from
 * ticket_workflow_notes under its own RLS — there is no second notes store.
 */
export async function fetchChainNotes(ticketId: string): Promise<{ notes: WorkflowNote[]; chain: NoteChainMember[] }> {
  const { data: chainData, error: chainError } = await supabase.rpc('ticket_note_chain' as never, { p_ticket_id: ticketId } as never);
  if (chainError) throw chainError;
  let chain = ((chainData as unknown as NoteChainMember[] | null) ?? []);
  if (!chain.some((c) => c.ticket_id === ticketId)) {
    // The server answers only for tickets the caller may see; the open ticket is always its own day.
    chain = [...chain, { ticket_id: ticketId, chain_position: chain.length + 1, simple_ticket_number: null, scheduled_date: null, is_current: true, status: null }];
  }
  const ids = chain.map((c) => c.ticket_id);
  const { data, error } = await supabase
    .from('ticket_workflow_notes')
    .select('*')
    .in('ticket_id', ids)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const notes = (data || []) as unknown as WorkflowNote[];
  const people = [...new Set(notes.flatMap((n) => [n.created_by, n.edited_by, n.deleted_by]).filter(Boolean))] as string[];
  const names = await resolveAuthorNames(people);
  const byId = new Map(chain.map((c) => [c.ticket_id, c]));
  return {
    chain,
    notes: orderNotes(notes).map((n) => ({
      ...n,
      author_name: n.created_by ? names[n.created_by] : undefined,
      editor_name: n.edited_by ? names[n.edited_by] : undefined,
      deleter_name: n.deleted_by ? names[n.deleted_by] : undefined,
      origin: byId.get(n.ticket_id),
    })),
  };
}

export async function fetchNoteRevisions(noteId: string): Promise<WorkflowNoteRevision[]> {
  const { data, error } = await supabase
    .from('ticket_workflow_note_revisions' as never)
    .select('*')
    .eq('note_id', noteId)
    .order('revision_no', { ascending: true });
  if (error) throw error;
  const revs = (data || []) as unknown as WorkflowNoteRevision[];
  const names = await resolveAuthorNames([...new Set(revs.map((r) => r.editor_id))]);
  return revs.map((r) => ({ ...r, editor_name: names[r.editor_id] }));
}

export interface WorkflowInspection {
  id: string;
  workflow_report_id: string;
  ticket_id: string;
  shop_id: string;
  performed_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  overall_notes: string | null;
  created_at: string;
  performer_name?: string;
  items: WorkflowInspectionItem[];
}

export interface WorkflowInspectionItem {
  id: string;
  inspection_id: string;
  label: string;
  status: 'green' | 'yellow' | 'red';
  notes: string | null;
  template_item_id: string | null;
}

export interface WorkflowDiagnostic {
  id: string;
  workflow_report_id: string;
  ticket_id: string;
  shop_id: string;
  title: string | null;
  performed_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  concern: string | null;
  conditions: string | null;
  complaint_confirmed: boolean;
  dtc_list: string | null;
  scan_details: string | null;
  tsb_refs: string | null;
  service_info_refs: string | null;
  findings: string | null;
  root_cause: string | null;
  recommendation: string | null;
  confidence: number | null;
  created_at: string;
  performer_name?: string;
  tests: WorkflowDiagnosticTest[];
}

export interface WorkflowDiagnosticTest {
  id: string;
  diagnostic_id: string;
  test_name: string;
  result: string | null;
  values: Record<string, unknown> | null;
  notes: string | null;
}

async function resolveAuthorNames(ids: string[]): Promise<Record<string, string>> {
  if (ids.length === 0) return {};
  const { data } = await supabase
    .from('profiles')
    .select('id, display_name')
    .in('id', ids);
  if (!data) return {};
  return Object.fromEntries(data.map(p => [p.id, p.display_name || 'Unknown']));
}

export function useWorkflowReport(ticketId: string | null, shopId: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['workflow-report', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['workflow-complaints', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['workflow-notes', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['workflow-inspections', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['workflow-diagnostics', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['thread-events'] });
  };

  // Fetch or create workflow report
  const reportQuery = useQuery({
    queryKey: ['workflow-report', ticketId],
    queryFn: async (): Promise<WorkflowReport | null> => {
      if (!ticketId || !shopId) return null;

      // Try to fetch existing
      const { data: existing, error: readError } = await supabase
        .from('ticket_workflow_reports')
        .select('*')
        .eq('ticket_id', ticketId)
        .maybeSingle();

      // CORE-AE: a failed read is not "no report yet". Creating one here on a read error made a
      // second report for a ticket that already had one; surface the failure instead.
      if (readError) throw readError;

      if (existing) return existing as unknown as WorkflowReport;

      // Create new report
      const { data: created, error } = await supabase
        .from('ticket_workflow_reports')
        .insert({
          ticket_id: ticketId,
          shop_id: shopId,
          created_by: userId,
          updated_by: userId,
        } as any)
        .select()
        .single();

      if (error) {
        console.error('[useWorkflowReport] Failed to create report:', error);
        return null;
      }

      const report = created as unknown as WorkflowReport;

      // Emit initialization event
      logTicketEventToOcis({
        ticketId,
        shopId,
        eventType: 'workflow_report_initialized',
        content: 'Workflow report initialized',
        authorUserId: userId,
      });

      // Seed complaint from customer_request_notes
      const { data: ticket } = await supabase
        .from('tickets')
        .select('customer_request_notes')
        .eq('id', ticketId)
        .single();

      const customerNotes = (ticket as any)?.customer_request_notes;
      if (customerNotes && customerNotes.trim()) {
        await supabase
          .from('ticket_workflow_complaints')
          .insert({
            workflow_report_id: report.id,
            ticket_id: ticketId,
            shop_id: shopId,
            text: customerNotes.trim(),
            created_by: userId,
            updated_by: userId,
          } as any);

        logTicketEventToOcis({
          ticketId,
          shopId,
          eventType: 'workflow_complaint_added',
          content: `Customer concern seeded: "${customerNotes.trim().slice(0, 100)}"`,
          authorUserId: userId,
        });
      }

      return report;
    },
    enabled: !!ticketId && !!shopId,
  });

  const reportId = reportQuery.data?.id;

  // Complaints
  const complaintsQuery = useQuery({
    queryKey: ['workflow-complaints', ticketId],
    queryFn: async (): Promise<WorkflowComplaint[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_workflow_complaints')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const complaints = (data || []) as unknown as WorkflowComplaint[];
      const authorIds = [...new Set(complaints.map(c => c.created_by).filter(Boolean))] as string[];
      const names = await resolveAuthorNames(authorIds);
      return complaints.map(c => ({ ...c, author_name: c.created_by ? names[c.created_by] : undefined }));
    },
    enabled: !!ticketId && !!reportId,
  });

  // Notes — FD-09: every day of the rollover chain, in one chronological list.
  const notesQuery = useQuery({
    queryKey: ['workflow-notes', ticketId],
    queryFn: async () => {
      if (!ticketId) return { notes: [] as WorkflowNote[], chain: [] as NoteChainMember[] };
      return fetchChainNotes(ticketId);
    },
    enabled: !!ticketId && !!reportId,
  });
  // A note added or changed on one day shows on every other day of the same chain.
  const invalidateNotes = () => {
    invalidateAll();
    queryClient.invalidateQueries({ queryKey: ['workflow-notes'] });
    queryClient.invalidateQueries({ queryKey: ['workflow-note-revisions'] });
  };

  // Inspections (with items)
  const inspectionsQuery = useQuery({
    queryKey: ['workflow-inspections', ticketId],
    queryFn: async (): Promise<WorkflowInspection[]> => {
      if (!ticketId) return [];
      const { data: inspections, error } = await supabase
        .from('ticket_workflow_inspections')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (!inspections?.length) return [];

      const inspIds = inspections.map(i => (i as any).id);
      const { data: items } = await supabase
        .from('ticket_workflow_inspection_items')
        .select('id, inspection_id, label, status, notes, template_item_id')
        .in('inspection_id', inspIds)
        .order('created_at', { ascending: true });

      const performerIds = [...new Set(inspections.map((i: any) => i.performed_by).filter(Boolean))];
      const names = await resolveAuthorNames(performerIds);

      return inspections.map((insp: any) => ({
        ...insp,
        performer_name: insp.performed_by ? names[insp.performed_by] : undefined,
        items: (items || []).filter((it: any) => it.inspection_id === insp.id) as WorkflowInspectionItem[],
      })) as WorkflowInspection[];
    },
    enabled: !!ticketId && !!reportId,
  });

  // Diagnostics (with tests)
  const diagnosticsQuery = useQuery({
    queryKey: ['workflow-diagnostics', ticketId],
    queryFn: async (): Promise<WorkflowDiagnostic[]> => {
      if (!ticketId) return [];
      const { data: allDiags, error } = await supabase
        .from('ticket_workflow_diagnostics')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      // oct20a S1: technician evidence entries are append-only history (read in the Findings timeline),
      // never rows of this editable report.
      const diags = (allDiags ?? []).filter((d: any) => !d.evidence_authority);
      if (!diags.length) return [];

      const diagIds = diags.map((d: any) => d.id);
      const { data: tests } = await supabase
        .from('ticket_workflow_diagnostic_tests')
        .select('id, diagnostic_id, test_name, result, values, notes')
        .in('diagnostic_id', diagIds)
        .order('created_at', { ascending: true });

      const performerIds = [...new Set(diags.map((d: any) => d.performed_by).filter(Boolean))];
      const names = await resolveAuthorNames(performerIds);

      return diags.map((d: any) => ({
        ...d,
        performer_name: d.performed_by ? names[d.performed_by] : undefined,
        tests: (tests || []).filter((t: any) => t.diagnostic_id === d.id) as WorkflowDiagnosticTest[],
      })) as WorkflowDiagnostic[];
    },
    enabled: !!ticketId && !!reportId,
  });

  // === Mutations ===

  const addComplaint = useMutation({
    mutationFn: async (text: string) => {
      if (!reportId || !ticketId || !shopId) throw new Error('Missing context');
      const { error } = await supabase
        .from('ticket_workflow_complaints')
        .insert({ workflow_report_id: reportId, ticket_id: ticketId, shop_id: shopId, text, created_by: userId, updated_by: userId } as any);
      if (error) throw error;
      logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_complaint_added', content: `Complaint added: "${text.slice(0, 100)}"`, authorUserId: userId });
    },
    onSuccess: invalidateAll,
  });

  const updateComplaint = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { error } = await supabase
        .from('ticket_workflow_complaints')
        .update({ text, updated_by: userId } as any)
        .eq('id', id);
      if (error) throw error;
      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_complaint_updated', content: `Complaint updated: "${text.slice(0, 100)}"`, authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const addNote = useMutation({
    mutationFn: async (noteText: string) => {
      if (!reportId || !ticketId || !shopId) throw new Error('Missing context');
      const { error } = await supabase
        .from('ticket_workflow_notes')
        .insert({ workflow_report_id: reportId, ticket_id: ticketId, shop_id: shopId, note_text: noteText, created_by: userId, updated_by: userId } as any);
      if (error) throw error;
      logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_note_added', content: `Note added: "${noteText.slice(0, 100)}"`, authorUserId: userId });
    },
    onSuccess: invalidateNotes,
  });

  // FD-10: a note changes only through the governed RPCs (author or Manager/Owner, reason required, previous text
  // kept as a revision). Direct table UPDATE/DELETE is refused by the server.
  const noteChangeFailed = (verb: string) => (err: unknown) => {
    const msg = (err as { message?: string })?.message ?? '';
    toast({
      title: `Could not ${verb} the note`,
      description: /NOTE_EDIT_FORBIDDEN/.test(msg)
        ? 'Only the note\'s author, a Manager or an Owner can change it.'
        : /NOTE_REASON_REQUIRED/.test(msg) ? 'A reason is required.' : msg || 'Please try again.',
      variant: 'destructive',
    });
  };

  const updateNote = useMutation({
    mutationFn: async ({ id, noteText, reason, noteTicketId }: { id: string; noteText: string; reason: string; noteTicketId?: string }) => {
      const { data, error } = await supabase.rpc('edit_workflow_note' as never, { p_note_id: id, p_note_text: noteText, p_reason: reason } as never);
      if (error) throw error;
      const changed = (data as unknown as { changed?: boolean } | null)?.changed !== false;
      if (changed && shopId) {
        logTicketEventToOcis({ ticketId: noteTicketId ?? ticketId!, shopId, eventType: 'workflow_note_updated',
          content: `Note edited (${reason.slice(0, 80)}): "${noteText.slice(0, 100)}"`, authorUserId: userId });
      }
    },
    onSuccess: invalidateNotes,
    onError: noteChangeFailed('save'),
  });

  const deleteNote = useMutation({
    mutationFn: async ({ id, reason, noteTicketId }: { id: string; reason: string; noteTicketId?: string }) => {
      const { data, error } = await supabase.rpc('delete_workflow_note' as never, { p_note_id: id, p_reason: reason } as never);
      if (error) throw error;
      const changed = (data as unknown as { changed?: boolean } | null)?.changed !== false;
      if (changed && shopId) {
        logTicketEventToOcis({ ticketId: noteTicketId ?? ticketId!, shopId, eventType: 'workflow_note_updated', // existing OCIS type (no enum/Edge change); the revision row is the record
          content: `Note deleted (${reason.slice(0, 80)})`, authorUserId: userId });
      }
    },
    onSuccess: invalidateNotes,
    onError: noteChangeFailed('delete'),
  });

  const startInspection = useMutation({
    mutationFn: async () => {
      if (!reportId || !ticketId || !shopId) throw new Error('Missing context');

      // Load template to stamp metadata
      const template = await checkActiveTemplate(shopId, 'DVI');
      let bookTimeMin: number | null = null;
      if (template) {
        const { data: tplData } = await supabase
          .from('inspection_templates')
          .select('settings')
          .eq('id', template.templateId)
          .single();
        // No invented book time: a template that states none has none (CORE-AJ: unknown remains unknown).
        const stated = (tplData as any)?.settings?.book_time_min;
        bookTimeMin = typeof stated === 'number' && stated > 0 ? stated : null;
      }

      // Core-100 R4 (CORE-AJ): the inspection is created IDLE and the one timer authority starts it on the server
      // clock (inspection_timer_transition). The browser writes no started_at, timer state or session.
      const { data: inspection, error } = await supabase
        .from('ticket_workflow_inspections')
        .insert({
          workflow_report_id: reportId,
          ticket_id: ticketId,
          shop_id: shopId,
          performed_by: userId,
          created_by: userId,
          updated_by: userId,
          template_id: template?.templateId ?? null,
          template_version: template?.version ?? null,
          book_time_min: bookTimeMin,
        } as any)
        .select()
        .single();
      if (error) throw error;

      const inspId = (inspection as any).id;
      const { error: startErr } = await supabase.rpc('inspection_timer_transition' as never, {
        p_inspection_id: inspId, p_action: 'start', p_pause_detail: null,
      } as never);
      if (startErr) throw startErr;

      // Load template items from DB
      if (template) {
        const { data: templateItems } = await supabase
          .from('inspection_template_items')
          .select('id, name')
          .eq('template_id', template.templateId)
          .order('sort_order');

        if (templateItems?.length) {
          const itemInserts = templateItems.map((ti: any) => ({
            inspection_id: inspId,
            workflow_report_id: reportId,
            ticket_id: ticketId,
            shop_id: shopId,
            template_item_id: ti.id,
            label: ti.name,
            status: 'green',
            created_by: userId,
            updated_by: userId,
          }));
          await supabase.from('ticket_workflow_inspection_items').insert(itemInserts as any);
        }
      }

      logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_inspection_started', content: 'Inspection started', authorUserId: userId });
      return inspection;
    },
    onSuccess: invalidateAll,
  });

  const saveInspectionItems = useMutation({
    mutationFn: async ({ inspectionId, items }: { inspectionId: string; items: { id: string; status: string; notes: string | null }[] }) => {
      for (const item of items) {
        await supabase
          .from('ticket_workflow_inspection_items')
          .update({ status: item.status, notes: item.notes, updated_by: userId } as any)
          .eq('id', item.id);
      }
      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_inspection_saved', content: 'Inspection saved', authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const completeInspection = useMutation({
    mutationFn: async (inspectionId: string) => {
      // Core-100 R4 (CORE-AJ): completion closes any running session and records the MEASURED total through the
      // one timer authority — never a device-clock duration, never wall time from start to finish.
      const { data: done, error } = await supabase.rpc('inspection_timer_transition' as never, {
        p_inspection_id: inspectionId, p_action: 'complete', p_pause_detail: null,
      } as never);
      if (error) throw error;
      const totalDuration = Number((done as unknown as { total_duration_seconds?: number } | null)?.total_duration_seconds ?? 0);

      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_inspection_completed', content: `Inspection completed (${Math.round(totalDuration / 60)} min)`, authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const createDiagnostic = useMutation({
    mutationFn: async (concern?: string) => {
      if (!reportId || !ticketId || !shopId) throw new Error('Missing context');

      // Get current count for stable title
      const { count } = await supabase
        .from('ticket_workflow_diagnostics')
        .select('id', { count: 'exact', head: true })
        .eq('ticket_id', ticketId);

      const title = `Diag #${(count || 0) + 1}`;

      const { data, error } = await supabase
        .from('ticket_workflow_diagnostics')
        .insert({
          workflow_report_id: reportId,
          ticket_id: ticketId,
          shop_id: shopId,
          title,
          concern: concern || null,
          created_by: userId,
          updated_by: userId,
        } as any)
        .select()
        .single();
      if (error) throw error;

      logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_diagnostic_created', content: `Diagnostic session "${title}" created`, authorUserId: userId });
      return data;
    },
    onSuccess: invalidateAll,
  });

  const updateDiagnostic = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase
        .from('ticket_workflow_diagnostics')
        .update({ ...updates, updated_by: userId } as any)
        .eq('id', id);
      if (error) throw error;
      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_diagnostic_updated', content: 'Diagnostic updated', authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const startDiagnostic = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ticket_workflow_diagnostics')
        .update({ started_at: clock.now().toISOString(), performed_by: userId, updated_by: userId } as any)
        .eq('id', id);
      if (error) throw error;
      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_diagnostic_started', content: 'Diagnostic started', authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const completeDiagnostic = useMutation({
    mutationFn: async (id: string) => {
      const now = clock.now();
      const { data: diag } = await supabase
        .from('ticket_workflow_diagnostics')
        .select('started_at')
        .eq('id', id)
        .single();

      const startedAt = (diag as any)?.started_at ? new Date((diag as any).started_at) : now;
      const durationSeconds = Math.round((now.getTime() - startedAt.getTime()) / 1000);

      const { error } = await supabase
        .from('ticket_workflow_diagnostics')
        .update({ completed_at: now.toISOString(), duration_seconds: durationSeconds, updated_by: userId } as any)
        .eq('id', id);
      if (error) throw error;

      if (ticketId && shopId) {
        logTicketEventToOcis({ ticketId, shopId, eventType: 'workflow_diagnostic_completed', content: `Diagnostic completed (${Math.round(durationSeconds / 60)} min)`, authorUserId: userId });
      }
    },
    onSuccess: invalidateAll,
  });

  const addTest = useMutation({
    mutationFn: async ({ diagnosticId, testName }: { diagnosticId: string; testName: string }) => {
      if (!reportId || !ticketId || !shopId) throw new Error('Missing context');
      const { error } = await supabase
        .from('ticket_workflow_diagnostic_tests')
        .insert({ diagnostic_id: diagnosticId, workflow_report_id: reportId, ticket_id: ticketId, shop_id: shopId, test_name: testName, created_by: userId, updated_by: userId } as any);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const updateTest = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase
        .from('ticket_workflow_diagnostic_tests')
        .update({ ...updates, updated_by: userId } as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const removeTest = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ticket_workflow_diagnostic_tests')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  return {
    report: reportQuery.data || null,
    complaints: complaintsQuery.data || [],
    notes: notesQuery.data?.notes || [],
    noteChain: notesQuery.data?.chain || [],
    inspections: inspectionsQuery.data || [],
    diagnostics: diagnosticsQuery.data || [],
    isLoading: reportQuery.isLoading,
    // CORE-AE: the sections above fall back to [] — these say which of them FAILED to load, so the
    // tab can tell "nothing recorded" from "could not be read".
    loadFailures: ([
      ['Report', reportQuery.isError],
      ['Complaints', complaintsQuery.isError],
      ['Notes', notesQuery.isError],
      ['Inspections', inspectionsQuery.isError],
      ['Diagnostics', diagnosticsQuery.isError],
    ] as const).filter(([, failed]) => failed).map(([name]) => name),
    retryLoad: () => {
      void reportQuery.refetch();
      void complaintsQuery.refetch();
      void notesQuery.refetch();
      void inspectionsQuery.refetch();
      void diagnosticsQuery.refetch();
    },
    // Mutations
    addComplaint,
    updateComplaint,
    addNote,
    updateNote,
    deleteNote,
    startInspection,
    saveInspectionItems,
    completeInspection,
    createDiagnostic,
    updateDiagnostic,
    startDiagnostic,
    completeDiagnostic,
    addTest,
    updateTest,
    removeTest,
  };
}
